/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useState } from 'react';
import { storageApiRef, useApi } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import useObservable from 'react-use/lib/useObservable';
import { Box, IconButton, Tooltip, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExtensionIcon from '@material-ui/icons/ExtensionOutlined';
import { buildClients } from '../mcpConnect/clients';
import { claudeMcpCommand, claudePluginCommand, codexAddCommand, codexPluginCommand } from '../mcpConnect/links';
import { BANNER_ANALYTICS, McpInstallPanel } from '../mcpConnect/McpInstallPanel';
import { BACKSTAGE_M42, BACKSTAGE_URL } from '../mcpConnect/servers';

const STORAGE_BUCKET = 'home';
// Dismissal is a snooze, not a goodbye (PLAT-1823): the close button stores
// a snooze-until timestamp and the banner returns after a day. The legacy
// boolean key from the permanent-dismiss era is deliberately ignored, so
// earlier dismissers see the banner once more and then get snooze behavior.
export const SNOOZE_KEY = 'm42-plugin-banner-snooze-until';
export const SNOOZE_MS = 24 * 60 * 60 * 1000;

/** Every tab stated these two facts in its own words — per-user OAuth, and what
 * the tools reach. Said once above the strip instead, because four paraphrases
 * of one claim is how wording drifts apart. Everything inside a tab is specific
 * to that client. */
export const SHARED_LEDE =
  'One MCP server for every client — catalog, network policy and scaffolder ' +
  'runs, signed in as you. Per-user OAuth, no shared tokens.';

export const MCP_URL = BACKSTAGE_URL;
export const INSTALL_COMMAND = claudePluginCommand(BACKSTAGE_M42.plugin!);
export const MCP_ONLY_COMMAND = claudeMcpCommand(BACKSTAGE_M42.name, MCP_URL);
export const CODEX_PLUGIN_COMMAND = codexPluginCommand(BACKSTAGE_M42.plugin!);
export const CODEX_ADD_COMMAND = codexAddCommand(BACKSTAGE_M42.name, MCP_URL);
export const { clients: EDITOR_CLIENTS, groups: EDITOR_GROUPS } = buildClients(BACKSTAGE_M42);

const useStyles = makeStyles(theme => ({
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    // Owns its bottom spacing so dismissal leaves no phantom gap (the
    // component renders null; a caller-side wrapper margin would remain).
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    // Same subtle accent tint family as ReadOnlyAccessNotice -- promotional,
    // not an alert.
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 110, 91, 0.10)'
        : 'rgba(255, 110, 91, 0.07)',
    // On a phone the icon and close columns ran the banner's full height and
    // left the tab strip 227px of the 297px content width — too little for
    // four vendors, so Codex hid behind the scroll arrow. Here they share the
    // title row only, and everything beneath spans the width (the body's
    // children join this grid). rowGap 0: the flex `gap` above would otherwise
    // drop 12px between title, lede, tabs and panel.
    [theme.breakpoints.down('xs')]: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      columnGap: theme.spacing(1.5),
      rowGap: 0,
    },
  },
  icon: {
    color: theme.palette.primary.main,
    marginTop: 2,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: { gridArea: '1 / 1' },
  },
  body: {
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down('xs')]: { display: 'contents' },
  },
  title: {
    fontWeight: 600,
    fontSize: '0.9375rem',
    [theme.breakpoints.down('xs')]: { gridArea: '1 / 2' },
  },
  fullBleed: {
    [theme.breakpoints.down('xs')]: { gridColumn: '1 / -1' },
  },
  lede: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  dismiss: {
    padding: theme.spacing(0.5),
    marginTop: -theme.spacing(0.5),
    marginRight: -theme.spacing(0.5),
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: { gridArea: '1 / 3' },
  },
}));
/**
 * Promotional banner for working with this Backstage from an AI editor
 * (PLAT-1812). Sits in Home's fixed above-the-grid zone; one tab per vendor,
 * with Claude Code and Claude Desktop picked inside the Claude tab. Claude Code gets the full backstage-m42 plugin (tools +
 * skills + agents + MCP); Cursor and OpenCode reuse the same MCP server via
 * their own config file, Codex via the same plugin or `codex mcp add`, and Claude Desktop/claude.ai
 * via a pasted URL. Every client ships unqualified. Neither Cursor nor OpenCode needs an explicit
 * scope any more: both read `scopes_supported` off the protected-resource
 * metadata and ask for `offline_access` themselves. OpenCode only ever needed
 * the line because the server did not advertise it before plugin 0.2.1 — with
 * nothing configured it now builds `scope=openid+offline_access` unprompted,
 * measured 2026-09-08 under a server name its config had never seen so nothing
 * could merge a scope in. docs/mcp-oauth.md has the detail.
 *
 * Claude Desktop (and claude.ai, the same Connectors list) takes a pasted
 * server URL instead of a config file — Anthropic's cloud dials the server, so
 * nothing local is configured.
 *
 * Editors with a deep link get a "1-Click Connect" primary button (Cursor's
 * and Claude Code's observed 2026-09-04, Codex's 2026-09-24): Cursor's opens Cursor's own install
 * confirm for the server; Claude Code's opens a new session with the install
 * request pre-filled — Enter and a Bash approval still follow, and the scheme
 * exists only after Claude Code's first interactive prompt. Codex prefills
 * either setup's commands through the ChatGPT app's open-app link. OpenCode
 * has no scheme, so its
 * tab keeps copy-the-config as the only path. Claude Desktop has
 * no INSTALL deep link — Anthropic publishes none for the Connectors surface —
 * but its documented chat link still starts the sign-in, because a prompt naming
 * Backstage makes Claude Desktop offer the connector's own Connect card.
 *
 * The snippet block is a live miniature terminal: the active tab's setup
 * types itself in (click or any focus entering the terminal completes it
 * instantly; reduced motion renders it whole), and a copy prints a response
 * line of durable facts into an always-reserved row (no layout shift). A
 * blocked clipboard selects the text and says what to press instead of
 * failing silently.
 *
 * Closing the banner snoozes it for a day (SNOOZE_KEY in the `home`
 * storage bucket, optimistic local hide) rather than dismissing it forever
 * — there is no other way back to the install command. Copies and tab
 * switches are captured through the surrounding AnalyticsContext.
 */
export const M42PluginBanner = () => {
  const classes = useStyles();
  const storage = useApi(storageApiRef).forBucket(STORAGE_BUCKET);
  const snap = useObservable(
    storage.observe$<number>(SNOOZE_KEY),
    storage.snapshot<number>(SNOOZE_KEY),
  );
  const [hidden, setHidden] = useState(false);
  const snoozedUntil = typeof snap?.value === 'number' ? snap.value : 0;
  if (hidden || Date.now() < snoozedUntil) return null;

  const dismiss = () => {
    setHidden(true);
    storage.set(SNOOZE_KEY, Date.now() + SNOOZE_MS);
  };

  return (
    <Box className={classes.banner}>
      <ExtensionIcon className={classes.icon} fontSize="small" />
      <Box className={classes.body}>
        <Typography component="div" className={classes.title}>
          Work with this Backstage from your AI editor
        </Typography>
        <Typography component="div" className={`${classes.lede} ${classes.fullBleed}`}>
          {SHARED_LEDE}
        </Typography>
        {/* Its own element: the banner suite matches SHARED_LEDE exactly. */}
        <Typography component="div" className={`${classes.lede} ${classes.fullBleed}`}>
          <Link to="/mcp-catalog">Browse all MCP servers →</Link>
        </Typography>
        <div className={classes.fullBleed}>
          <McpInstallPanel server={BACKSTAGE_M42} analytics={BANNER_ANALYTICS} idPrefix="m42-editor" />
        </div>
      </Box>
      {/* The label says what actually happens — a snooze, not a goodbye. */}
      <Tooltip title="Hide for a day">
        <IconButton
          className={classes.dismiss}
          size="small"
          aria-label="Hide AI editor banner for a day"
          onClick={dismiss}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
